import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { formatMoney } from "@/lib/api";
import { renderBrandedHeader, renderBrandedFooter, brandedTableHeadStyles, hexToRgb, drawSignatureBlock } from "@/lib/pdfBranding";

const COLORS = {
  text:    [26, 28, 26],
  muted:   [110, 115, 110],
  border:  [209, 209, 203],
};

export function generateBudgetPdf(budget, patient, branding, signer = null) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const margin = 48;

  let y = renderBrandedHeader(doc, branding, { title: "PRESUPUESTO", subtitle: budget.number || "" });

  // Patient block
  y += 8;
  doc.setTextColor(...COLORS.muted);
  doc.setFontSize(8);
  doc.text("PACIENTE", margin, y);
  doc.text("CONTACTO", W / 2, y);
  y += 14;
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.text);
  doc.setFontSize(13);
  doc.text(patient?.name || "—", margin, y);
  doc.setFontSize(11);
  doc.text(patient?.phone || "—", W / 2, y);
  y += 16;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.muted);
  const docLine = patient?.document_number
    ? `${patient.document_type || "CC"} ${patient.document_number}`
    : "Sin documento";
  doc.text(docLine, margin, y);
  doc.text(patient?.email || "—", W / 2, y);
  y += 12;
  if (patient?.eps) {
    doc.text(`EPS · ${patient.eps}`, margin, y);
    y += 12;
  }
  if (patient?.address || patient?.city) {
    doc.text([patient.address, patient.city].filter(Boolean).join(" · "), W / 2, y - 12);
  }

  // Items table
  y += 10;
  const tableY = y;
  autoTable(doc, {
    startY: tableY,
    margin: { left: margin, right: margin },
    head: [["Descripción", "Diente", "Cant.", "Valor unit.", "Total"]],
    body: budget.items.map((it) => [
      it.description,
      it.tooth || "—",
      String(it.qty || 1),
      formatMoney(it.unit_price),
      formatMoney(it.total ?? (it.qty || 1) * (it.unit_price || 0)),
    ]),
    theme: "grid",
    styles: {
      font: "helvetica",
      fontSize: 9,
      cellPadding: 6,
      textColor: COLORS.text,
      lineColor: COLORS.border,
      lineWidth: 0.5,
    },
    headStyles: {
      ...brandedTableHeadStyles(branding),
      halign: "left",
      fontSize: 8,
      fontStyle: "bold",
    },
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { cellWidth: 50, halign: "center" },
      2: { cellWidth: 40, halign: "center" },
      3: { cellWidth: 90, halign: "right" },
      4: { cellWidth: 100, halign: "right" },
    },
    alternateRowStyles: { fillColor: [247, 247, 245] },
  });

  // Totals
  let ty = doc.lastAutoTable.finalY + 18;
  const totalsX = W - margin - 220;

  const row = (label, value, bold = false) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(bold ? 11 : 9);
    doc.setTextColor(...(bold ? COLORS.text : COLORS.muted));
    doc.text(label, totalsX, ty);
    doc.setTextColor(...COLORS.text);
    doc.text(value, W - margin, ty, { align: "right" });
    ty += bold ? 18 : 14;
  };
  row("Subtotal", formatMoney(budget.subtotal));
  if (budget.discount > 0) row("Descuento", "- " + formatMoney(budget.discount));
  if (budget.tax_pct > 0) row(`IVA (${budget.tax_pct}%)`, formatMoney(budget.tax_amount));

  // Total banner con color del branding
  ty += 4;
  const primary = hexToRgb(branding?.color_primario || "#1B4332");
  doc.setFillColor(...primary);
  doc.rect(totalsX - 12, ty - 16, W - margin - totalsX + 12, 28, "F");
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.text("TOTAL", totalsX, ty);
  doc.setFontSize(14);
  doc.text(formatMoney(budget.total), W - margin, ty, { align: "right" });
  ty += 26;

  // Validity / notes
  if (budget.valid_until) {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLORS.muted);
    doc.setFontSize(9);
    doc.text(`Vigencia hasta: ${budget.valid_until}`, margin, ty);
    ty += 14;
  }
  if (budget.notes) {
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.text);
    doc.setFontSize(9);
    doc.text("Notas:", margin, ty);
    ty += 12;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLORS.muted);
    const wrapped = doc.splitTextToSize(budget.notes, W - margin * 2);
    doc.text(wrapped, margin, ty);
    ty += wrapped.length * 11;
  }

  // Bloque de firma del profesional
  const sigBaseY = H - 100;
  drawSignatureBlock(doc, {
    x: margin,
    baselineY: sigBaseY,
    width: 240,
    signatureImage: signer?.signature_image || null,
    label: "Firma del profesional",
    name: signer?.name || budget.created_by_name || "",
    subtitle: signer?.signature_text || signer?.registry_number || "",
  });

  // Footer
  doc.setDrawColor(...COLORS.border);
  doc.line(margin, H - 70, W - margin, H - 70);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.muted);
  doc.text(
    "Este presupuesto es informativo y puede variar según diagnóstico y procedimientos adicionales realizados.",
    margin, H - 54
  );
  doc.text(`Atendido por: ${signer?.name || budget.created_by_name || "—"}`, margin, H - 38);
  renderBrandedFooter(doc, branding);

  doc.save(`${budget.number || "presupuesto"}_${(patient?.name || "paciente").replace(/\s+/g, "_")}.pdf`);
}
